import { ref, onMounted, computed } from "vue";
import useMessageListener from '@/composables/messageListener';

export type A365Theme = 'light' | 'dark' | 'dark-contrast';
export type A365ThemeCode = A365Theme | 'system';

const ThemeCookieName = 'a365-color-theme';

const SystemDarkThemeQuery = '(prefers-color-scheme: dark)';

const currentTheme = ref<A365Theme>();

export default function useA365Themes(themeSwitchCallback?: (theme: A365Theme) => void) {
  useMessageListener('theme-changed', handleThemeSwitch);

  onMounted(() => {
    if (currentTheme.value == null)
      setThemeFromCookie();
  });

  function handleThemeSwitch(themeCode: string) {
    const newTheme = setThemeByCode(themeCode);
    if (newTheme != null && themeSwitchCallback != null)
      themeSwitchCallback(newTheme);
  }

  function setThemeFromCookie() {
    const themeCode = getCookieValue(ThemeCookieName);
    return setThemeByCode(themeCode);
  }

  return {
    currentTheme: computed((): A365Theme => currentTheme.value ?? 'dark'),
    setThemeFromCookie,
    setTheme,
  };
}

function setThemeByCode(themeCode?: string) {
  const parsedThemeCode = parseThemeCode(themeCode);
  if (parsedThemeCode == null) {
    console.warn(`Unsupported theme code: ${themeCode}`);
    return undefined;
  }

  return setTheme(resolveTheme(parsedThemeCode));
}

function setTheme(theme: A365Theme) {
  if (theme === currentTheme.value)
    return undefined;

  currentTheme.value = theme;
  return theme;
}

function resolveTheme(themeCode: A365ThemeCode): A365Theme {
  if (themeCode !== 'system')
    return themeCode;

  return window.matchMedia?.(SystemDarkThemeQuery).matches ? 'dark' : 'light';
}

function getCookieValue(name: string) {
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");

  if (parts.length == 2) {
    return parts.pop()?.split(";").shift();
  }
}

function parseThemeCode(themeCode?: string): A365ThemeCode | undefined {
  switch (themeCode) {
    case 'dark':
      return 'dark';
    case 'dark-contrast':
      return 'dark-contrast';
    case 'light':
      return 'light';
    case 'system':
      return 'system';
    default:
      return undefined;
  }
}
